<script setup>
/**
 * GiftReply: the end of the gift letter, the recipient's one reply
 * (2026-10-22, gift redesign §7; mockups R2a / R2 / R2b).
 *
 * One cream card under a gold seal: "WRITE BACK TO YOUR SON", the server's
 * question ("Did your son get it right?"), five crystals with the chosen word
 * under them, a line back, and one button. A crystal OR words enable it; no
 * "Not now" (it is the end of the letter, not a modal). One tap saves the
 * rating (`rateReading`, via 'gift') and the thanks (`careThank`, with the
 * rating). Then it seals: what they said, and that the sender will see it.
 *
 * `inert`: the send screen's preview draws it, not pressable.
 * `inline` (round 2, DESIGN-2 §39): the gift room's one form, in the chat
 * where the chips were. It has a quiet "Not now" (`dismiss`), and sealed it
 * is their own sent message, right-aligned: "TO YOUR SON · 🔮… Exactly right ·
 * "…" · ✓ Sent to your son's Messages".
 * The words come from the server (`respond`), with the fallbacks in
 * `care-room.replyText`. No pronoun for the sender, ever.
 */
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { store } from '@/store'
import { rateReading, careThank } from '@/api-service'
import { replyText } from '@/utils/care-room'

const props = defineProps({
  gift: { type: Object, default: () => ({}) },
  // The server's words for the room (care-package/open's `room`): `respond`,
  // `sender_ref`, `replied` (her write-back, on a reload).
  room: { type: Object, default: () => ({}) },
  // The package's short id (the rating) and its invite (the thanks).
  short: { type: String, default: '' },
  inv: { type: String, default: '' },
  // Already written back (this visit, or a reload): { rating, note } or true.
  sealed: { type: [Object, Boolean], default: null },
  inert: { type: Boolean, default: false },
  inline: { type: Boolean, default: false },
  // The room's door before the first act (a guest place, or the email door):
  // resolves true when she may write back now.
  beforeSend: { type: Function, default: null },
  // Called when it went (as well as 'sent'): the first act can remount the
  // room (a guest place is made), and the page must still hear it.
  onSent: { type: Function, default: null },
})
const emit = defineEmits(['sent', 'dismiss'])

const t = computed(() => replyText(props.gift, (props.room && props.room.respond) || {}, props.room || {}))
const hovered = ref(0)
const rating = ref(0)
const note = ref('')
const sending = ref(false)
const error = ref('')
const done = ref(null)          // { rating, note } once sent
const fieldEl = ref(null)

const served = computed(() => (props.room && props.room.replied) || null)
const sealedNow = computed(() => done.value || served.value
  || (props.sealed ? (typeof props.sealed === 'object' ? props.sealed : {}) : null))
const sealedRating = computed(() => Number((sealedNow.value && sealedNow.value.rating)
  || (store.reading_ratings || {})[props.short] || 0))
const shownWord = computed(() => t.value.words[(hovered.value || rating.value) - 1] || '')
const canSend = computed(() => !props.inert && !sending.value && (rating.value > 0 || note.value.trim().length > 0))

watch(() => props.sealed, (v) => { if (!v) done.value = null })

// The draft (e2e round 2): what they started to write back survives a reload
// or the room's ✕, per invite, for this visit (sessionStorage). Storage may
// be absent or refuse; the card never notices.
const draftKey = computed(() => (props.inv ? `gift-reply-draft:${props.inv}` : ''))
function readDraft () {
  if (props.inert || !draftKey.value) return null
  try {
    const raw = sessionStorage.getItem(draftKey.value)
    const d = raw ? JSON.parse(raw) : null
    return d && typeof d === 'object' ? d : null
  } catch (e) { return null }
}
function writeDraft () {
  if (props.inert || !draftKey.value || done.value) return
  try {
    if (!note.value && !rating.value) sessionStorage.removeItem(draftKey.value)
    else sessionStorage.setItem(draftKey.value, JSON.stringify({ note: note.value, rating: rating.value }))
  } catch (e) { /* no storage */ }
}
function dropDraft () {
  if (!draftKey.value) return
  try { sessionStorage.removeItem(draftKey.value) } catch (e) { /* no storage */ }
}
const kept = sealedNow.value ? null : readDraft()
if (kept) {
  if (typeof kept.note === 'string') note.value = kept.note
  const n = Number(kept.rating)
  if (Number.isInteger(n) && n >= 1 && n <= 5) rating.value = n
}
watch([note, rating], writeDraft)

function pick(n) {
  if (props.inert || sending.value) return
  rating.value = rating.value === n ? 0 : n
  error.value = ''
}

async function send() {
  if (!canSend.value) return
  sending.value = true
  error.value = ''
  const n = rating.value
  const line = note.value.trim()
  if (props.beforeSend) {
    let ok = false
    try { ok = await props.beforeSend() } catch (e) { ok = false }
    if (!ok) { sending.value = false; return }
  }
  let thanked = null
  try {
    if (n && props.short) {
      await rateReading(props.short, n, store.session_id, { via: 'gift' })
      store.reading_ratings = { ...(store.reading_ratings || {}), [props.short]: n }
    }
    thanked = await careThank(props.inv, store.session_id, line, n ? { rating: n } : {})
  } catch (e) {
    const msg = e && e.response && e.response.data && (e.response.data.message || e.response.data.error)
    error.value = msg || 'That did not go through. Try again.'
    sending.value = false
    return
  }
  sending.value = false
  done.value = { rating: n, note: line }
  dropDraft()
  // The Mystic's answer, keyed to the rating (API-2 §2: thank's `line`).
  // `circle_ask`: the Mystic's one ask that follows ("Add <sender> to your
  // circle?", 2026-10-26), null when not owed.
  const said = { rating: n, note: line, line: String((thanked && thanked.line) || ''),
                 circle_ask: (thanked && thanked.circle_ask) || null }
  if (props.onSent) props.onSent(said)
  else emit('sent', said)
}

// The chip "Write back to your son" brings the card up and puts the cursor in it.
function focus() {
  const el = fieldEl.value
  if (el && el.focus) { try { el.focus({ preventScroll: true }) } catch (e) { el.focus() } }
}
defineExpose({ focus })
// Opened in the chat on a computer: the cursor is in the field (a phone
// keeps its keyboard down until they tap, so the crystals stay in view).
onMounted(() => {
  if (!props.inline || props.inert || sealedNow.value) return
  const fine = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches
  if (fine) nextTick(focus)
})
</script>

<template>
  <section class="gr" :class="{ 'is-sealed': !!sealedNow, 'is-inert': inert, 'gr-inline': inline, 'gr-inline-sent': inline && !!sealedNow }"
           :data-testid="inline && sealedNow ? 'gift-reply-sent' : 'gift-reply'"
           :aria-disabled="inert ? 'true' : null" :inert="inert ? '' : null">
    <span v-if="!(inline && sealedNow)" class="gr-seal" aria-hidden="true">✦</span>

    <template v-if="!sealedNow">
      <p class="gr-eyebrow">{{ t.eyebrow }}</p>
      <h2 class="gr-ask" data-testid="gift-reply-ask">{{ t.ask }}</h2>
      <div class="gr-balls" role="radiogroup" :aria-label="t.ask">
        <button v-for="n in 5" :key="n" type="button" class="gr-ball" role="radio"
                :class="{ filled: n <= (hovered || rating) }" :aria-checked="n === rating ? 'true' : 'false'"
                :aria-label="t.words[n - 1]" :tabindex="inert ? -1 : 0" :disabled="inert"
                :data-testid="`gift-reply-ball-${n}`"
                @mouseenter="hovered = n" @mouseleave="hovered = 0" @focus="hovered = n" @blur="hovered = 0"
                @click="pick(n)">🔮</button>
      </div>
      <p class="gr-word" aria-live="polite">{{ shownWord }}</p>
      <!-- Inert (the send screen's preview): the empty field as it will look, not a field. -->
      <span v-if="inert" class="gr-field gr-field-shown" data-testid="gift-reply-note">{{ t.placeholder }}</span>
      <textarea v-else ref="fieldEl" v-model="note" class="gr-field" rows="3" maxlength="500"
                :placeholder="t.placeholder" data-testid="gift-reply-note" @input="error = ''"></textarea>
      <p v-if="error" class="gr-error" role="alert" data-testid="gift-reply-error">{{ error }}</p>
      <button type="button" class="gr-send" :disabled="!canSend" :tabindex="inert ? -1 : 0"
              data-testid="gift-reply-send" @click="send">
        <i class="fas" :class="sending ? 'fa-spinner fa-spin' : 'fa-heart'" aria-hidden="true"></i>
        <span>{{ t.button }}</span>
      </button>
      <button v-if="inline" type="button" class="gr-not-now" data-testid="gift-reply-not-now"
              @click="emit('dismiss')">Not now</button>
    </template>

    <!-- Sent, in the chat: their own message (§39). -->
    <template v-else-if="inline">
      <p class="gr-eyebrow">{{ t.sentEyebrow }}</p>
      <p v-if="sealedRating" class="gr-said" data-testid="gift-reply-sealed-rating">
        <span class="gr-said-balls" aria-hidden="true"><span v-for="n in sealedRating" :key="n">🔮</span></span>
        <span class="gr-said-word">{{ t.words[sealedRating - 1] }}</span>
      </p>
      <p v-if="sealedNow.note" class="gr-line" data-testid="gift-reply-sealed-note">“{{ sealedNow.note }}”</p>
      <p class="gr-sent" data-testid="gift-reply-sealed"><span class="gr-tick" aria-hidden="true">✓</span> {{ t.sealedNote }}</p>
    </template>

    <template v-else>
      <p class="gr-eyebrow">{{ t.sealedEyebrow }}</p>
      <p class="gr-ask-small">{{ t.ask }}</p>
      <p v-if="sealedRating" class="gr-said" data-testid="gift-reply-sealed-rating">
        <span class="gr-said-balls" aria-hidden="true"><span v-for="n in sealedRating" :key="n">🔮</span></span>
        <span class="gr-said-word">{{ t.words[sealedRating - 1] }}</span>
      </p>
      <p v-if="sealedNow.note" class="gr-line" data-testid="gift-reply-sealed-note">“{{ sealedNow.note }}”</p>
      <p class="gr-sent" data-testid="gift-reply-sealed"><span class="gr-tick" aria-hidden="true">✓</span> {{ t.sealedNote }}</p>
    </template>
  </section>
</template>

<style scoped>
.gr {
  --paper2: #fbf6ec; --paper-edge: #e4d6b8; --gold-dk: #8a6a2a; --ink: #2a1845; --hush: #7a6a8f;
  --display: 'Letter Display', 'Cinzel', Georgia, serif;
  --serif: 'Letter Serif', 'Cormorant Garamond', Georgia, serif;
  --ui: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  position: relative; margin: 26px 14px 14px; padding: 30px 20px 22px; text-align: center;
  background: var(--paper2); border: 1px solid var(--paper-edge); border-radius: 16px;
  box-shadow: 0 18px 30px -22px rgba(42, 24, 69, 0.7); color: var(--ink);
  scroll-margin-top: 16px;
}
.gr-seal {
  position: absolute; top: -17px; left: 50%; transform: translateX(-50%);
  width: 34px; height: 34px; border-radius: 50%; display: grid; place-items: center;
  background: radial-gradient(circle at 35% 30%, #f3d98f, #c9a94e 45%, #8a6a2a 100%);
  box-shadow: 0 3px 8px rgba(42, 24, 69, 0.45), inset 0 0 0 3px rgba(255, 240, 200, 0.35);
  color: #5a3f0f; font: 700 14px/1 var(--display);
}
.gr-eyebrow { margin: 4px 0 10px; font: 600 11px/1.4 var(--display); letter-spacing: 0.2em; text-transform: uppercase; color: var(--gold-dk); text-wrap: balance; }
.gr-ask { margin: 0 0 14px; font: 600 26px/1.2 var(--serif); color: var(--ink); text-wrap: balance; }
.gr-balls { display: flex; justify-content: center; gap: 14px; }
.gr-ball {
  width: 44px; height: 44px; padding: 0; border: 0; background: none; cursor: pointer;
  font-size: 32px; line-height: 1; filter: grayscale(1) opacity(0.45);
  transition: transform 0.12s ease, filter 0.12s ease;
}
.gr-ball.filled { filter: none; }
.gr-ball:hover { transform: translateY(-2px) scale(1.06); }
.gr-ball:focus-visible { outline: 2px solid #7b4bb0; outline-offset: 2px; border-radius: 50%; }
.gr-word { margin: 6px 0 12px; min-height: 1.4em; font: italic 500 17px/1.4 var(--serif); color: var(--gold-dk); }
.gr-field {
  display: block; width: 100%; box-sizing: border-box; resize: vertical; min-height: 92px;
  padding: 12px 14px; border-radius: 12px; border: 1.5px solid #c9b6ee; background: #fff;
  font: italic 500 17px/1.45 var(--serif); color: var(--ink); text-align: left;
}
.gr-field::placeholder, .gr-field-shown { color: #9a8bb0; }
.gr-field:focus { outline: none; border-color: #7b4bb0; box-shadow: 0 0 0 4px rgba(123, 75, 176, 0.18); }
.gr-error { margin: 8px 0 0; font: 600 13px/1.4 var(--ui); color: #b4622a; }
.gr-send {
  display: inline-flex; align-items: center; justify-content: center; gap: 10px;
  width: 100%; min-height: 52px; margin-top: 14px; padding: 0 20px; border-radius: 999px; cursor: pointer;
  border: 1px solid rgba(240, 217, 163, 0.55); background: linear-gradient(180deg, #7b4bb0, #4e2776);
  color: #ffdfa3; font: 700 16px/1 var(--ui); box-shadow: 0 12px 26px -12px rgba(78, 39, 118, 0.7);
}
.gr-send i { color: #f0d9a3; }
.gr-send:disabled { opacity: 0.5; cursor: default; box-shadow: none; }
.gr-send:focus-visible { outline: 2px solid #7b4bb0; outline-offset: 3px; }
.gr.is-inert .gr-ball, .gr.is-inert .gr-send, .gr.is-inert .gr-field { pointer-events: none; }

/* Sealed. */
.gr-ask-small { margin: 0 0 6px; font: 500 15px/1.4 var(--ui); color: var(--hush); }
.gr-said { margin: 0 0 10px; display: flex; align-items: center; justify-content: center; flex-wrap: wrap; gap: 6px 14px; }
.gr-said-balls { font-size: 20px; letter-spacing: 1px; }
.gr-said-word { font: 600 24px/1.2 var(--serif); color: var(--ink); }
.gr-line { margin: 0 0 12px; font: italic 500 18px/1.5 var(--serif); color: var(--ink); text-wrap: pretty; overflow-wrap: anywhere; }
.gr-sent { margin: 0; display: inline-flex; align-items: center; gap: 8px; font: 600 14.5px/1.3 var(--ui); color: var(--gold-dk); }
.gr-tick {
  display: inline-grid; place-items: center; width: 22px; height: 22px; border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #f3d98f, #c9a94e 55%, #8a6a2a); color: #3a2a08; font-size: 12px;
}

@container (min-width: 620px) {
  .gr { margin: 30px auto 24px; max-width: 560px; padding: 34px 32px 26px; }
  .gr-ask { font-size: 30px; }
}
/* In the chat (§39): the card where the chips were, then their sent message. */
.gr.gr-inline { box-sizing: border-box; width: 100%; max-width: 560px; margin: 26px auto 10px; scroll-margin-bottom: 12px; }
.gr-not-now {
  display: block; margin: 10px auto 0; padding: 8px 14px; min-height: 36px; border: 0; background: none; cursor: pointer;
  font: 600 13.5px/1 var(--ui); color: var(--hush);
}
.gr-not-now { position: relative; }
.gr-not-now::after { content: ''; position: absolute; inset: -5px 0; }
.gr-not-now:hover { color: var(--ink); text-decoration: underline; }
.gr-not-now:focus-visible { outline: 2px solid #7b4bb0; outline-offset: 2px; border-radius: 8px; }
.gr.gr-inline-sent {
  width: auto; align-self: flex-end; margin: 10px 0 10px auto; max-width: min(86%, 520px); padding: 14px 16px 12px; text-align: left;
  border-radius: 18px 18px 4px 18px; box-shadow: none;
}
.gr-inline-sent .gr-eyebrow { margin: 0 0 6px; font-size: 10px; letter-spacing: 0.18em; }
.gr-inline-sent .gr-said { justify-content: flex-start; margin: 0 0 6px; gap: 4px 12px; }
.gr-inline-sent .gr-said-balls { font-size: 15px; }
.gr-inline-sent .gr-said-word { font: 700 18px/1.2 var(--serif); }
.gr-inline-sent .gr-line { margin: 0 0 8px; font-size: 17px; line-height: 1.4; color: #3d2f55; }
.gr-inline-sent .gr-sent { font-size: 12.5px; gap: 6px; }
.gr-inline-sent .gr-tick { width: 18px; height: 18px; font-size: 10px; }

@media (prefers-reduced-motion: reduce) { .gr-ball { transition: none; } }
</style>
